/* global window */
import _ from 'lodash';
import { getElementDefinitionProps } from 'crazyEditor/core/elements/utils/elementDefinitionUtils';
import { getDesktopDOMNodeById, getMobileDOMNodeById } from 'crazyEditor/core/elements/utils/elementRefUtils';
import { isChildElement, isRichTextInsideTabsElement } from 'crazyEditor/core/elements/utils/elementFilterUtils';
import { store } from 'crazyEditor/redux/store';
import { getDragDelta, isMobileView as isMobileViewSelector, getStageLayout as getStageLayoutSelector, getElementsForView} from 'crazyEditor/redux/modules/element/elementSelectors';
import { elementTypes } from 'crazyEditor/components/Elements/index';
import { getElementMethods } from 'crazyEditor/core/elements/utils/elementDefinitionUtils';

const isRichTextInsideTabsElementVisible = (element) => {
  const elementMethods = getElementMethods(elementTypes.TabsElement);
  return element.id === elementMethods.getSelectedTabId(element.parentId);
};

function getAbsoluteElementPosition(elementLayout, boundingLayout = null) {
  const stageLayout = getStageLayoutSelector(store.getState());
  const x = elementLayout.x + stageLayout.left;
  const y = elementLayout.y + stageLayout.editorTop;
  return {
    x: boundingLayout ? x - boundingLayout.left : x,
    y: boundingLayout ? y - boundingLayout.top : y
  };
}

function convertAbsolutePositionToRelative(position) {
  const stageLayout = getStageLayoutSelector(store.getState());
  return {
    x: position.x - stageLayout.left,
    y: position.y - stageLayout.editorTop + (window.scrollY || window.pageYOffset),
  };
}

function isMousePositionInsideElementBoundaries(mousePosition, absElementPosition, elementLayout, includeHoverBoxCloseButton = false) {
  if (includeHoverBoxCloseButton && false) { // temporarily disabled, waiting for semi to lehisager on himself for CL-2379
    if (mousePosition.x >= (absElementPosition.x + elementLayout.width - 60) &&
      mousePosition.x <= (absElementPosition.x + elementLayout.width) &&
      mousePosition.y >= absElementPosition.y - 30 &&
      mousePosition.y <= absElementPosition.y) {
      return true;
    }
  }
  return mousePosition.x >= absElementPosition.x &&
    mousePosition.x <= (absElementPosition.x + elementLayout.width) &&
    mousePosition.y >= absElementPosition.y &&
    mousePosition.y <= (absElementPosition.y + elementLayout.height);
}

function getElementLayout(element, isMobileView) {
  const elementLayout = _.assign({}, element.layout);

  if (isRichTextInsideTabsElement(element)) {
    // This is a very tightly coupled use case for RichTextElement inside TabsElement
    // We might want a different behavior for other elements
    const elementMethods = getElementMethods(elementTypes.TabsElement);
    const elements = getElementsForView(store.getState());
    const parentElement = elements[element.parentId];
    _.assign(elementLayout, {
      y: parentElement.layout.y + elementMethods.getChildrenOffset(element.parentId),
      x: parentElement.layout.x,
      height: parentElement.layout.height - elementMethods.getChildrenOffset(element.parentId),
      width: parentElement.layout.width
    });

  }

  return elementLayout;
}

function getElementStageLayout(element, boundingLayout) {
  const isMobileView = isMobileViewSelector(store.getState());
  const stageLayout = getStageLayoutSelector(store.getState());
  const elementLayout = getElementLayout(element, isMobileView);
  const absElementPosition = getAbsoluteElementPosition(elementLayout, boundingLayout);
  const elementDefinitionProps = getElementDefinitionProps(element);

  return {
    top: absElementPosition.y - stageLayout.top,
    left: elementDefinitionProps.fullWidth ? absElementPosition.x : absElementPosition.x,
    width: elementDefinitionProps.fullWidth ? stageLayout.width : elementLayout.width,
    height: elementLayout.height,
  };
}

function getElementsBoundingLayout(elements) {
  let top = 0;
  let left = 0;
  let maxWidth = 0;
  let maxHeight = 0;

  _.forEach(elements, (element, index) => {
    const elementLayout = getElementStageLayout(element);
    if (index === 0) {
      top = elementLayout.top;
      left = elementLayout.left;
      maxWidth = elementLayout.left + elementLayout.width;
      maxHeight = elementLayout.top + elementLayout.height;
    } else {
      top = Math.min(top, elementLayout.top);
      left = Math.min(left, elementLayout.left);
      maxWidth = Math.max(maxWidth, elementLayout.left + elementLayout.width);
      maxHeight = Math.max(maxHeight, elementLayout.top + elementLayout.height);
    }
  });

  return {top, left, width: maxWidth - left, height: maxHeight - top};
}

function getElementUnderMousePosition(elements, mousePosition, isMobileView, preferredId, includeHoverBoxCloseButton = false, hoveredElementId = null) {
  function getPreferredElement(element1, element2) {
    if (element1.parentId) {
      if (element1.parentId === preferredId || element1.id === preferredId) {
        return element1;
      }
      return element2;
    }
    if (element2.parentId) {
      if (element2.parentId === preferredId || element2.id === preferredId) {
        return element2;
      }
      return element1;
    }
    if (element1.compProps.zIndex > element2.compProps.zIndex) {
      return element1;
    }
    return element2;
  }
  const elementsFound = [];
  _.forEach(elements, (element) => {
    if (!isChildElement(element) || (isRichTextInsideTabsElement(element) && isRichTextInsideTabsElementVisible(element))) {
      const elementLayout = getElementLayout(element, isMobileView);
      const absElementPosition = getAbsoluteElementPosition(elementLayout);
      if (isMousePositionInsideElementBoundaries(mousePosition, absElementPosition, elementLayout, !isChildElement(element) && includeHoverBoxCloseButton && element.id === hoveredElementId)) {
        elementsFound.push(element);
      }
    }
  });

  return elementsFound.length ? _.reduce(elementsFound, getPreferredElement) : null;
}

function isElementInsideRectBoundaries(rectLayout, absElementPosition, elementLayout) {
  return (absElementPosition.x + elementLayout.width) < (rectLayout.x + rectLayout.width)
    && absElementPosition.x > rectLayout.x
    && absElementPosition.y > rectLayout.y
    && (absElementPosition.y + elementLayout.height) < (rectLayout.y + rectLayout.height);
}

function getElementsWithinRect(elements, rectX, rectY, rectWidth, rectHeight) {
  const isMobileView = isMobileViewSelector(store.getState());
  const stageLayout = getStageLayoutSelector(store.getState());
  const elementsFound = [];
  const rectLayout = {
    x: rectX,
    y: rectY,
    width: rectWidth,
    height: rectHeight
  };
  _.forEach(elements, (element) => {
    const elementLayout = getElementLayout(element, isMobileView);
    const absElementPosition = getAbsoluteElementPosition(elementLayout);
    if (!element.parentId && isElementInsideRectBoundaries(rectLayout, absElementPosition, elementLayout)) {
      elementsFound.push({
        id: element.id,
        absLayout: {
          x: absElementPosition.x,
          y: absElementPosition.y - stageLayout.top + 1 // border-size
        },
        layout: {
          width: elementLayout.width,
          height: elementLayout.height
        }
      });
    }
  });

  return elementsFound;
}

function isCkeditorToolbar(element) {
  let nextElement = element;
  while (nextElement) {
    if (nextElement.classList && (nextElement.classList.contains('cke_inner') || nextElement.classList.contains('cke_dialog') || nextElement.classList.contains('cke_dialog_background_cover'))) {
      return true;
    }
    nextElement = nextElement.parentElement;
  }
  return false;
}

function getDragDeltaFromStore() {
  return getDragDelta(store.getState());
}

export {
  convertAbsolutePositionToRelative,
  getAbsoluteElementPosition,
  getElementStageLayout,
  getElementUnderMousePosition,
  getElementsBoundingLayout,
  getElementsWithinRect,
  isCkeditorToolbar,
  getDragDeltaFromStore,
};
